import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { getGroupMessages } from '../../services/api';
import toast from 'react-hot-toast';
import { MessageCircle, Send, Users, ExternalLink, CheckCircle } from 'lucide-react';
// @ts-ignore
import Spinner from '../../components/Spinner';

interface Message {
  text: string;
  sender_id: string;
  sender_name?: string;
  sender_handle?: string;
  avatarUrl?: string;
  timestamp: string;
}

const LiveChatView: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const { socket, isConnected } = useSocket();
  
  const [selectedGroupId, setSelectedGroupId] = useState<string>('general');
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Receive new messages via WebSocket
  useEffect(() => {
    if (!socket) return undefined;
    
    const handleNewMessage = (message: Message) => {
      if (message.sender_id === currentUser?.uid) {
        return;
      }
      setMessages(prev => [...prev, message]);
    };
    
    socket.on('new_message', handleNewMessage);
    return () => {
      socket.off('new_message', handleNewMessage);
    };
  }, [socket, currentUser]);

  // Load chat history and join group
  useEffect(() => {
    if (!selectedGroupId || !currentUser || !socket || !isConnected) {
      setIsLoadingHistory(false);
      return undefined;
    }

    const fetchHistoryAndJoin = async () => {
      setIsLoadingHistory(true);
      try {
        const token = await currentUser.getIdToken();
        const data = await getGroupMessages(token, selectedGroupId);
        setMessages(data.messages || []);
        
        socket.emit('join_group', { groupId: selectedGroupId, token: token });
      } catch (error: any) {
        toast.error(`Could not load chat history: ${error.message}`);
      } finally {
        setIsLoadingHistory(false);
      }
    };
    
    fetchHistoryAndJoin();

    return () => {
      if (socket?.connected) {
        socket.emit('leave_group', { groupId: selectedGroupId });
      }
    };
  }, [selectedGroupId, currentUser, socket, isConnected]);

  // Auto-scroll to new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Send message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !isConnected || !currentUser) return;

    try {
      const token = await currentUser.getIdToken();
      const optimisticMessage: Message = {
        text: newMessage,
        sender_id: currentUser.uid,
        sender_name: userProfile?.displayName || currentUser.displayName || 'User',
        sender_handle: userProfile?.handle,
        avatarUrl: userProfile?.avatarUrl || `https://ui-avatars.com/api/?name=${currentUser.displayName}`,
        timestamp: new Date().toISOString(),
      };

      // Optimistic UI update
      setMessages(prev => [...prev, optimisticMessage]);
      
      socket?.emit('send_message', {
        groupId: selectedGroupId,
        text: newMessage,
        token: token
      });
      
      setNewMessage('');
    } catch (error: any) {
      toast.error(`Failed to send message: ${error.message}`);
    }
  };

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Talkoru Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-3">
          <MessageCircle size={20} />
          <span className="text-sm font-medium">
            💬 Want to chat with <strong>67+ AI experts</strong>? Try Talkoru!
          </span>
        </div>
        <a
          href="https://www.talkoru.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-bold transition-colors"
        >
          Visit Talkoru <ExternalLink size={14} />
        </a>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-slate-200">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Users className="text-blue-600" size={28} />
            Live Group Chat
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Real-time conversations with the community
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
            isConnected 
              ? 'bg-emerald-100 text-emerald-700' 
              : 'bg-slate-100 text-slate-500'
          }`}>
            <CheckCircle size={12} className={isConnected ? '' : 'opacity-50'} />
            {isConnected ? 'Connected' : 'Connecting...'}
          </div>
        </div>
      </div>

      {/* Chat Container */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {isLoadingHistory ? (
            <div className="flex items-center justify-center h-full">
              <Spinner />
            </div>
          ) : messages.length > 0 ? (
            messages.map((msg, index) => {
              if (!msg || typeof msg.text === 'undefined') return null;

              const prevMessage = messages[index - 1];
              const showSenderInfo = !prevMessage || prevMessage.sender_id !== msg.sender_id;
              const isOwnMessage = msg.sender_id === currentUser?.uid;

              const senderName = msg.sender_name || 'Unknown User';
              const avatarUrl = msg.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(senderName)}&background=random`;

              return (
                <div
                  key={index}
                  className={`flex gap-3 ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    {!isOwnMessage && showSenderInfo ? (
                      <img
                        src={avatarUrl}
                        alt={senderName}
                        className="w-8 h-8 rounded-full border border-slate-200"
                      />
                    ) : (
                      <div className="w-8" />
                    )}
                  </div>

                  {/* Message Content */}
                  <div className={`flex flex-col max-w-[70%] ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                    {!isOwnMessage && showSenderInfo && (
                      <div className="text-xs font-medium text-slate-600 mb-1 px-2">
                        {senderName}
                      </div>
                    )}
                    
                    <div
                      className={`px-4 py-2.5 rounded-2xl ${
                        isOwnMessage
                          ? 'bg-blue-600 text-white rounded-br-sm'
                          : 'bg-white border border-slate-200 text-slate-900 rounded-bl-sm'
                      }`}
                    >
                      <p className="text-sm leading-relaxed break-words">{msg.text}</p>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <MessageCircle size={48} className="text-slate-300 mb-3" />
              <p className="text-slate-600 font-medium">No messages yet</p>
              <p className="text-sm text-slate-400 mt-1">Be the first to say hello!</p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-slate-200 bg-white p-4">
          <form onSubmit={handleSendMessage} className="flex gap-3">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              disabled={!isConnected}
              className="flex-1 px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
            />
            <button
              type="submit"
              disabled={!isConnected || !newMessage.trim()}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${
                !isConnected || !newMessage.trim()
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
              }`}
            >
              <Send size={18} />
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LiveChatView;
